import React, { useMemo } from "react";
import type { ColourType } from "../../../../../../common-deprecated/types/CommonTypes";
import * as Styles from "./styles/ItemSlider";
import { isLightColour } from "../../../../../../common-deprecated/utils";
import { isItemFirstOfType } from "../../../utils/sliderUtils";
import ItemSlider, { type ItemSliderPropsType } from "./ItemSlider";
import * as Flex from "../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import useItemSliderSinglePage from "../../../../../../common-deprecated/hooks/useItemSliderSinglePage";

/**
 * This is a static version of the ItemSlider.
 * It takes the same arguments and renders the same elements minus the slider logic.
 * The list element will shrink based on the amount of items, which makes it useful when there are only a small number of them.
 */
const StaticItemSlider = ({ items, activeItem, activeItemType, selectItem }: ItemSliderPropsType): JSX.Element => {
    const renderedItems = useMemo(
        () => (
            <>
                {items.map((item, index) => {
                    const lightColour = !!(item as ColourType).rgb && isLightColour((item as ColourType).rgb);
                    const showDivider = index > 0 && isItemFirstOfType(items, index);
                    const isSelected = !!activeItem && item.id === activeItem.id;
                    return (
                        <Styles.ItemWrapper key={item.id} onClick={() => selectItem(item)} showDivider={showDivider}>
                            <Styles.ExteriorImage
                                image={item.image}
                                rgb={(item as ColourType).rgb}
                                isSelected={isSelected}
                                zoomedIn={activeItemType === "colour"}
                                lightColour={lightColour}
                            />
                        </Styles.ItemWrapper>
                    );
                })}
            </>
        ),
        // OR-9070: We added items.length because array gets re-assigned every time and this prevents pointless re-renders. However if the case ever occurs where a bug is reported that the items don't update when the length remains, please modify as follows:
        // declare const itemIds = items.map({id} => id).join() outside of this useMemo but in the component scope
        // replace items.length with the itemIds
        // alternatives are possible but that seems efficient to me
        [activeItem, activeItemType, items.length],
    );

    return (
        <Styles.Wrapper>
            <Styles.Slider>
                <Styles.SliderViewport>{renderedItems}</Styles.SliderViewport>
            </Styles.Slider>
        </Styles.Wrapper>
    );
};

const DynamicItemSlider = (props: ItemSliderPropsType): JSX.Element => {
    const displayStaticSlider = useItemSliderSinglePage(props.items.length, Styles.TOTAL_WIDTH, 1170, 30, 3 / 4);
    return displayStaticSlider ? (
        <Flex.Col
            mt={{ xs: 2, md: 3 }}
            pl={0}
            width={{ xs: 1, lg: "initial" }}
            maxWidth={{ lg: "75%" }} // maxWidth does not parse fractions as %
        >
            <StaticItemSlider {...props} />
        </Flex.Col>
    ) : (
        <Flex.Col mt={{ xs: 2, md: 3 }} px={0} width={{ xs: 1, lg: 3 / 4 }} flexGrow={1} maxWidth={{ lg: "75%" }}>
            <ItemSlider {...props} />
        </Flex.Col>
    );
};

export default DynamicItemSlider;
